import {Button, Tooltip} from "reactstrap";
import {IPermission, IPermissionGroup} from "../../../store/permissions/types";
import React, {useEffect, useState} from "react";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {PermissionsService} from "../../../services/permissions.service";
import classnames from "classnames";

interface PermissionGroupAccordionProps {
    groups: IPermissionGroup[];
    onAddPermission?: (selectedGroupId: number) => void
    onUpdateGroud?: (groupId: number) => void
    onUpdatePermission?: (permissionId: number) => void
}

interface PermissionAccordionBodyProps {
    groupId: number;
    permissions: IPermission[];
    onClickAddPermission?: () => void;
    onClickUpdatePermission?: (permissionId: number) => void;
    onClickUpdatePermissionGroup?: () => void;
    onSuccessRemoveGroup?: () => void;
}

interface AccordionBodyItemProps {
    permission: IPermission;
    onClickUpdatePermission?: (permissionId: number) => void;
}

const AccordionBodyItem: React.FC<AccordionBodyItemProps> = ({permission, onClickUpdatePermission}) => {
    const queryClient = useQueryClient();
    const [removedPermission, setRemovedPermission] = useState<boolean>(false);
    const [sureToRemove, setSureToRemove] = useState<boolean>(false);

    const [openTooltip, setOpenTooltip] = useState<boolean>(false);
    const [copied, setCopied] = useState<boolean>(false);

    const handleCopy = async (permission: IPermission) => {
        await navigator.clipboard.writeText(permission.name);

        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const toggleTooltip = () => setOpenTooltip(prev => !prev)

    const onSuccessRemove = () => {
        queryClient.invalidateQueries({queryKey: ["keyPermissionsGroups"]})
    }

    const {mutate: removePermission, isPending: isPendingRemove} = useMutation({
        mutationFn: () => PermissionsService.removePermission(permission.id),
        onSuccess: onSuccessRemove,
        mutationKey: ["keyRemovePermission", permission.id],
    });

    const handleRemoveClick = () => {
        if (!sureToRemove) {
            setSureToRemove(true)
        } else {
            setSureToRemove(false)
            setRemovedPermission(true)
            removePermission()
        }
    }

    useEffect(() => {
        if (sureToRemove) {
            const timer = setTimeout(() => setSureToRemove(false), 2500);
            return () => clearTimeout(timer);
        }
    }, [sureToRemove]);

    const handleUpdatePermission = () => {
        if (onClickUpdatePermission) onClickUpdatePermission(permission.id);
    }

    const isPendingDelete = isPendingRemove || (!!queryClient.isFetching() && removedPermission);

    return <div className="d-flex align-items-center justify-content-between gap-2 p-2 rounded-2 bg-light-subtle">
        <div className="d-flex align-items-center gap-1 text-truncate">
            <div id={"tooltip-" + permission.id}
                 className="d-flex align-items-center gap-1"
                 role="button" onClick={() => handleCopy(permission)}>

                <i className="bx bx-hash"/>
                <span className="text-dark fw-bold">{permission.title}</span>

                <Tooltip
                    key={copied ? "copied" : "name"}
                    placement="top"
                    isOpen={openTooltip}
                    target={"tooltip-" + permission.id}
                    toggle={toggleTooltip}
                >
                    {copied ? "Copied!" : permission.name}
                </Tooltip>
            </div>

            {permission.description && (
                <span style={{display: "contents"}}>
                    <span>–</span>
                    <span className="text-dark text-truncate">{permission.description}</span>
                </span>
            )}
        </div>

        <div className="d-flex align-items-center gap-2">
            <Button className="px-2 py-1" color="success" onClick={handleUpdatePermission}>
                <i className="bx bx-edit"></i>
            </Button>

            <Button className="px-2 py-1" color="danger" onClick={handleRemoveClick} disabled={isPendingDelete}>

                {isPendingDelete ? (
                        <i className="bx bx-loader"></i>) :
                    sureToRemove ? (<i className="bx bx-check"></i>) : (
                        <i className="bx bx-trash"></i>)}
            </Button>
        </div>
    </div>
}

const PermissionAccordionBody: React.FC<PermissionAccordionBodyProps> = ({
                                                                             groupId,
                                                                             permissions,
                                                                             onClickAddPermission,
                                                                             onClickUpdatePermission,
                                                                             onClickUpdatePermissionGroup,
                                                                             onSuccessRemoveGroup
                                                                         }) => {
    const queryClient = useQueryClient();
    const [removedGroup, setRemovedGroup] = useState<boolean>(false);
    const [sureToRemove, setSureToRemove] = useState<boolean>(false);

    const handleAddPermission = () => {
        if (onClickAddPermission) onClickAddPermission();
    }

    const handleUpdatePermissionGroup = () => {
        if (onClickUpdatePermissionGroup) onClickUpdatePermissionGroup();
    }

    const onSuccessRemove = () => {
        queryClient.invalidateQueries({queryKey: ["keyPermissionsGroups"]})
            .then(() => onSuccessRemoveGroup?.())
    }

    const {mutate: removeGroup, isPending: isPendingRemove} = useMutation({
        mutationFn: () => PermissionsService.removeGroup(groupId!),
        onSuccess: onSuccessRemove,
        mutationKey: ["keyRemovePermissionsGroup", groupId],
    });

    const handleRemoveClick = () => {
        if (!sureToRemove) {
            setSureToRemove(true)
        } else {
            setSureToRemove(false)
            setRemovedGroup(true)
            removeGroup()
        }
    }

    useEffect(() => {
        if (sureToRemove) {
            const timer = setTimeout(() => setSureToRemove(false), 2500);
            return () => clearTimeout(timer);
        }
    }, [sureToRemove]);

    const isUngrouped = groupId === -1;

    const isPendingDelete = isPendingRemove || (!!queryClient.isFetching() && removedGroup);

    return (
        <div className="px-3 pb-3 pt-1">
            <hr className="my-2 text-secondary opacity-25"/>
            <div className="d-flex flex-column gap-1">
                {permissions.length > 0 ?
                    permissions.map((permission) => (
                        <AccordionBodyItem key={permission.id} permission={permission}
                                           onClickUpdatePermission={onClickUpdatePermission}/>
                    )) : (
                        <span className="my-2 fs-5 text-center fst-italic text-secondary">
                            This permission group is empty!
                        </span>
                    )}
            </div>
            {!isUngrouped && (<>
                <hr className="my-2 text-secondary opacity-25"/>
                <div className="d-flex align-items-center justify-content-between">
                    <div>
                        <Button className="p-0 px-1 text-danger" color="link" onClick={handleRemoveClick}
                                disabled={isPendingDelete}>
                            {isPendingDelete ? "Removing..." : sureToRemove ? "Confirm" : "Remove"}
                        </Button>

                        <Button className="p-0 px-2" color="link"
                                onClick={handleUpdatePermissionGroup}>Edit</Button>
                    </div>
                    <Button className="p-0 px-2" color="link" onClick={handleAddPermission}>Add Permission</Button>
                </div>
            </>)}
        </div>
    )
}

const PermissionGroupAccordion: React.FC<PermissionGroupAccordionProps> = ({
                                                                               groups,
                                                                               onAddPermission,
                                                                               onUpdatePermission,
                                                                               onUpdateGroud
                                                                           }) => {
    const [openedGroup, setOpenedGroup] = useState<number | null>(null);

    const toggleAccordion = (id: number): void => setOpenedGroup(openedGroup === id ? null : id);

    return (
        <div className="py-4">
            <div className="row align-items-start m-0">
                {groups.map((group) => {
                    const isOpen = openedGroup === group.id;

                    return (
                        <div key={group.id} className="col-12 col-md-6 col-lg-4 px-2">
                            <div className="mb-3 card border-0 shadow-sm rounded-3 overflow-hidden">
                                {/* header */}
                                <button
                                    type="button"
                                    onClick={() => toggleAccordion(group.id)}
                                    className={`btn text-start w-100 border-0 rounded-0 p-2 d-flex align-items-center ${isOpen ? "bg-opacity-10 gap-3" : "bg-light gap-2"}`}
                                >
                                    <span
                                        className={`d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 ${isOpen ? "bg-primary text-white" : "bg-light text-secondary"}`}
                                        style={{width: 40, height: 40}}>
                                        <i className={`bx bx-${group.icon} fs-5`}/>
                                    </span>

                                    <span
                                        className="d-flex gap-2 align-items-center justify-content-between flex-grow-1">
                                        <span className="d-block fw-semibold text-dark">{group.name}</span>
                                        <span
                                            className={classnames("badge rounded-pill py-1", {
                                                "bg-primary": !!group.permissions.length,
                                                "text-muted fst-italic bg-transparent border border-danger": !group.permissions.length
                                            })}>
                                            {group.permissions.length || "Empty"}
                                        </span>
                                    </span>

                                    <i className="bx bx-chevron-down fs-4 text-secondary"
                                       style={{transform: isOpen ? "rotate(180deg)" : "none"}}/>
                                </button>

                                {/* body */}
                                <div className={isOpen ? "d-block" : "d-none"}>
                                    <PermissionAccordionBody groupId={group.id} permissions={group.permissions}
                                                             onClickAddPermission={() => onAddPermission?.(group.id)}
                                                             onClickUpdatePermission={onUpdatePermission}
                                                             onClickUpdatePermissionGroup={() => onUpdateGroud?.(group.id)}
                                                             onSuccessRemoveGroup={() => setOpenedGroup(null)}/>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    )
};

export default PermissionGroupAccordion;